import React, { useRef, useCallback, useState } from "react";
import {
  FileText,
  Image,
  FileSpreadsheet,
  File,
  Upload,
  X,
  RefreshCw
} from "lucide-react";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { useBookingPermissions } from "../../../../../hooks/useBookingPermissions";
import { useDocumentUpload } from "../../../../../hooks/useDocumentUpload";
import {
  getDocumentDownloadUrl,
  deleteDocument,
  ConflictError
} from "../../../../../APIs/Booking/DocumentOperations";
import { fetchBookingV4FromAPI } from "../../../../../APIs/Booking/GetBooking";
import { useOnlineStatus } from "../../../../../hooks/useOnlineStatus";
import { useDeleteConfirmation } from "../../../../../hooks/useDeleteConfirmation";
import { EDSButton } from "../../../../eds/EDSButton";
import { BookingDocument } from "../../../../../types/bookings";

const MAX_FILE_SIZE_BYTES = 8 * 1024 * 1024;

const ALLOWED_MIME_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "text/plain",
  "text/rtf",
  "application/rtf"
]);

const isAllowedFileType = (file: File): boolean => {
  if (ALLOWED_MIME_TYPES.has(file.type)) return true;
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  const allowedExtensions = new Set([
    "pdf",
    "doc",
    "docx",
    "xls",
    "xlsx",
    "csv",
    "jpg",
    "jpeg",
    "png",
    "gif",
    "webp",
    "txt",
    "rtf"
  ]);
  return allowedExtensions.has(extension);
};

const isSafeUrl = (url: string): boolean =>
  url.startsWith("https://") || url.startsWith("http://");

const getFileIcon = (mimeType: string): React.ReactNode => {
  if (mimeType.startsWith("image/")) return <Image aria-hidden="true" />;
  if (mimeType.includes("spreadsheet") || mimeType.includes("csv"))
    return <FileSpreadsheet aria-hidden="true" />;
  if (
    mimeType.includes("pdf") ||
    mimeType.includes("document") ||
    mimeType.includes("text")
  )
    return <FileText aria-hidden="true" />;
  return <File aria-hidden="true" />;
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric"
  });
};

const SelectedBookingDocuments = (): React.ReactElement => {
  const { selectedBookingV4, setDiaryContext } = useDiaryContext();
  const {
    userCanReadDocuments,
    userCanUpdateDocuments,
    userCanDownloadDocuments
  } = useBookingPermissions();
  const {
    upload,
    progress,
    isUploading,
    error: uploadError
  } = useDocumentUpload();
  const isOnline = useOnlineStatus();
  const { confirmingDeleteId, handleDeleteClick } = useDeleteConfirmation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileSizeError, setFileSizeError] = useState<string | null>(null);
  const [fileTypeError, setFileTypeError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [staleDataError, setStaleDataError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [offlineError, setOfflineError] = useState<string | null>(null);
  const [uploadFileIndex, setUploadFileIndex] = useState(0);
  const [uploadFileCount, setUploadFileCount] = useState(0);

  const documents = selectedBookingV4?.documents ?? [];

  const handleUploadClick = useCallback(() => {
    if (!isOnline) {
      setOfflineError("This action requires an internet connection.");
      return;
    }
    setOfflineError(null);
    fileInputRef.current?.click();
  }, [isOnline]);

  const handleDownload = useCallback(
    async (documentId: string) => {
      if (!selectedBookingV4?._id) return;

      if (!isOnline) {
        setOfflineError("This action requires an internet connection.");
        return;
      }
      setOfflineError(null);

      const url = await getDocumentDownloadUrl(
        selectedBookingV4._id,
        documentId
      );

      if (url && isSafeUrl(url)) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        setDeleteError("Failed to download document. Please try again.");
      }
    },
    [selectedBookingV4?._id, isOnline]
  );

  const handleDelete = useCallback(
    async (documentId: string) => {
      if (!selectedBookingV4?._id) return;

      if (!isOnline) {
        setOfflineError("This action requires an internet connection.");
        return;
      }
      setOfflineError(null);
      setDeleteError(null);
      setStaleDataError(null);

      try {
        const success = await deleteDocument(selectedBookingV4._id, documentId);

        if (success) {
          setDiaryContext((prev) => {
            const currentBooking = prev.selectedBookingV4;
            if (!currentBooking) return prev;

            return {
              ...prev,
              selectedBookingV4: {
                ...currentBooking,
                documents: (currentBooking.documents ?? []).filter(
                  (doc) => doc.id !== documentId
                )
              }
            };
          });
        }
      } catch (error) {
        if (error instanceof ConflictError) {
          setStaleDataError(error.message);
        } else {
          setDeleteError("Failed to delete document. Please try again.");
        }
      }
    },
    [selectedBookingV4?._id, setDiaryContext, isOnline]
  );

  const handleRefresh = useCallback(async () => {
    if (!selectedBookingV4?._id) return;

    setIsRefreshing(true);
    setStaleDataError(null);

    try {
      const refreshedBooking = await fetchBookingV4FromAPI(
        selectedBookingV4._id
      );

      if (refreshedBooking) {
        setDiaryContext((prev) => ({
          ...prev,
          selectedBookingV4: refreshedBooking
        }));
      }
    } catch {
      setStaleDataError("Failed to refresh booking data. Please try again.");
    } finally {
      setIsRefreshing(false);
    }
  }, [selectedBookingV4?._id, setDiaryContext]);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files?.length || !selectedBookingV4?._id) return;

      setFileSizeError(null);
      setFileTypeError(null);

      const fileArray = Array.from(files);
      const oversizedFiles = fileArray.filter(
        (file) => file.size > MAX_FILE_SIZE_BYTES
      );
      const invalidTypeFiles = fileArray.filter(
        (file) => !isAllowedFileType(file)
      );

      if (oversizedFiles.length > 0) {
        setFileSizeError(
          "Sorry, this file is too big. Maximum file size is 8 MB."
        );
      }

      if (invalidTypeFiles.length > 0) {
        setFileTypeError(
          "Unsupported file type. Please upload PDF, Word, Excel, CSV, or image files."
        );
      }

      const validFiles = fileArray.filter(
        (file) => file.size <= MAX_FILE_SIZE_BYTES && isAllowedFileType(file)
      );

      setUploadFileCount(validFiles.length);

      for (let i = 0; i < validFiles.length; i++) {
        setUploadFileIndex(i + 1);
        const newDocument = await upload(selectedBookingV4._id, validFiles[i]);

        if (newDocument) {
          setDiaryContext((prev) => {
            const currentBooking = prev.selectedBookingV4;
            if (!currentBooking) return prev;

            return {
              ...prev,
              selectedBookingV4: {
                ...currentBooking,
                documents: [...(currentBooking.documents ?? []), newDocument]
              }
            };
          });
        }
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [selectedBookingV4?._id, upload, setDiaryContext]
  );

  const validationErrors = [fileSizeError, fileTypeError].filter(Boolean);
  const displayError =
    offlineError ??
    (validationErrors.length > 0 ? validationErrors.join(" ") : null) ??
    uploadError ??
    deleteError;

  if (!userCanReadDocuments) {
    return <></>;
  }

  return (
    <div className="flex flex-col gap-2 p-4">
      {staleDataError && (
        <div
          role="alert"
          className="flex items-center gap-2 p-3 rounded-lg border bg-warning/10 border-warning/20 text-foreground text-sm"
        >
          <span className="flex-1">{staleDataError}</span>
          <EDSButton
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
            aria-label="Refresh booking data"
            className="min-h-[44px] min-w-[44px] shrink-0 focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            <RefreshCw
              aria-hidden="true"
              className={isRefreshing ? "animate-spin" : ""}
            />
            Refresh
          </EDSButton>
        </div>
      )}

      {userCanUpdateDocuments && (
        <div className="flex flex-col gap-2">
          <div className="flex justify-end">
            <EDSButton
              variant="outline"
              size="sm"
              onClick={handleUploadClick}
              disabled={isUploading}
              aria-label="Upload document"
              className="min-h-[44px] min-w-[44px] focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              <Upload aria-hidden="true" />
              Upload document
            </EDSButton>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.webp,.txt,.rtf"
              className="hidden"
              onChange={handleFileChange}
              aria-label="Choose files to upload"
              tabIndex={-1}
            />
          </div>

          {isUploading && (
            <div className="flex flex-col gap-1">
              {uploadFileCount > 1 && (
                <span className="text-xs text-muted-foreground">
                  Uploading {uploadFileIndex} of {uploadFileCount}...
                </span>
              )}
              <progress
                max={100}
                value={progress}
                aria-label={
                  uploadFileCount > 1
                    ? `Uploading file ${uploadFileIndex} of ${uploadFileCount}`
                    : "Uploading document"
                }
                className="w-full h-2 rounded-full overflow-hidden appearance-none [&::-webkit-progress-bar]:bg-muted [&::-webkit-progress-bar]:rounded-full [&::-webkit-progress-value]:bg-primary [&::-webkit-progress-value]:rounded-full [&::-webkit-progress-value]:transition-all [&::-webkit-progress-value]:duration-200 [&::-moz-progress-bar]:bg-primary [&::-moz-progress-bar]:rounded-full"
              />
            </div>
          )}
        </div>
      )}

      {displayError && (
        <p role="alert" className="text-sm text-destructive">
          {displayError}
        </p>
      )}

      {documents.length === 0 ? (
        <div className="flex items-center justify-center p-6 text-muted-foreground">
          <p>No documents have been added to this booking.</p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {documents.map((doc: BookingDocument) => (
            <li
              key={doc.id}
              className="flex items-center gap-3 p-3 rounded-lg bg-background min-h-[44px]"
            >
              <div className="flex-shrink-0 w-5 h-5 text-muted-foreground [&>svg]:w-5 [&>svg]:h-5">
                {getFileIcon(doc.mime_type)}
              </div>
              <div className="flex flex-col min-w-0 flex-1">
                {userCanDownloadDocuments ? (
                  <button
                    type="button"
                    onClick={() => handleDownload(doc.id)}
                    aria-label={`Download ${doc.filename}`}
                    className="text-sm font-medium truncate text-primary underline text-left cursor-pointer bg-transparent border-0 p-0 focus-visible:ring-2 focus-visible:ring-offset-2 rounded"
                  >
                    {doc.filename}
                  </button>
                ) : (
                  <span className="text-sm font-medium truncate text-foreground">
                    {doc.filename}
                  </span>
                )}
                <span className="text-xs text-muted-foreground">
                  {formatDate(doc.created_date)}
                </span>
              </div>
              {userCanUpdateDocuments && (
                <button
                  type="button"
                  onClick={() => handleDeleteClick(doc.id, handleDelete)}
                  aria-label={
                    confirmingDeleteId === doc.id
                      ? `Confirm delete ${doc.filename}`
                      : `Delete ${doc.filename}`
                  }
                  className="flex-shrink-0 flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg text-muted-foreground hover:text-destructive transition-colors focus-visible:ring-2 focus-visible:ring-offset-2"
                >
                  {confirmingDeleteId === doc.id ? (
                    <span
                      role="alert"
                      className="text-xs font-medium text-destructive"
                    >
                      Sure?
                    </span>
                  ) : (
                    <X aria-hidden="true" className="w-4 h-4" />
                  )}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SelectedBookingDocuments;
